// frontend/src/components/pages/chat/ChatSidebar.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  LogOut,
  Settings,
  Trash,
  ArrowLeft,
} from "lucide-react";
import DeleteThreadModal from "./DeleteThreadModal";
import {
  deleteThread,
  deleteAllThreads,
} from "../../../api/apiFunction/chatService";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import { LogoIcon, LogoWithText } from "../../Logo";
// import getTrialDetails API to fetch trial status on chat refresh
import { getTrialDetails } from "../../../api/apiFunction/userServices";
import { clearSessionData } from "../../../utils/deviceFingerprint";

const ChatSidebar = ({
  sidebarExpanded,
  setSidebarExpanded,
  searchQuery,
  setSearchQuery,
  chatHistory,
  setChatThreads,
  isLoadingHistory,
  refreshThreadList,
  handleChatSelect,
  createNewThread,
  showProfileDropdown,
  setShowProfileDropdown,
  profileDropdownRef,
  handleLogout,
  userData,
  currentThreadId,
  onCurrentThreadDeleted,
  onOpenTools,
  showSettingsModal,
  setShowSettingsModal,
  isAnonymous = false,
}) => {
  const { name, email } = userData || {};

  const [hoveredThread, setHoveredThread] = useState(null);
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const navigate = useNavigate();
  const [isDeletingThread, setIsDeletingThread] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [isDeletingAllThreads, setIsDeletingAllThreads] = useState(false);
  // track trial details and banner visibility
  const [trialInfo, setTrialInfo] = useState(null);
  const [hideTrialBanner, setHideTrialBanner] = useState(false);

  // fetch user's trial details on chat load/refresh
  // When the sidebar mounts, we get trial status from server (not localStorage)
  // Skip for anonymous users
  useEffect(() => {
    // Skip trial fetch for anonymous users
    let user = null;
    try {
      const userData = localStorage.getItem("user");
      user = userData ? JSON.parse(userData) : null;
    } catch (e) {}

    if (!user || !user.token) {
      // Anonymous user - skip trial fetch
      return;
    }

    let isMounted = true;
    const fetchTrial = async () => {
      try {
        const res = await getTrialDetails();
        if (!isMounted) return;

        const serverUser = res?.data?.user || null;
        const trial = serverUser?.trial || null;

        if (trial && typeof trial.daysRemaining === "number") {
          setTrialInfo({
            daysRemaining: trial.daysRemaining,
            endDate: trial.endDate,
            status: trial.status,
            currentDay: trial.currentDay,
          });

          // Update localStorage with fresh trial data ONLY if it changed
          // This prevents unnecessary updates that trigger re-renders
          const localUser = JSON.parse(localStorage.getItem("user")) || null;
          if (localUser) {
            const currentTrial = localUser.trial;
            const trialChanged =
              JSON.stringify(currentTrial) !== JSON.stringify(trial);

            // Only update if trial data actually changed
            if (trialChanged) {
              const mergedUser = { ...localUser, trial };
              localStorage.setItem("user", JSON.stringify(mergedUser));
            }
          }

          // COMMENTED OUT: Trial expiration redirect
          // This redirect is now handled by ProtectedRoute or other components
          // if (serverUser.role === "individual" && trial.daysRemaining === 0) {
          //     status: trial.status,
          //     daysRemaining: trial.daysRemaining,
          //     currentDay: trial.currentDay,
          //     endDate: trial.endDate,
          //   });
          //   navigate("/upgrade-plan", { replace: true });
          // }
        } else {
          setTrialInfo(null);
        }
      } catch (error) {
        // fail silently to avoid blocking chat
        if (isMounted) {
          setTrialInfo(null);
        }
      }
    };
    fetchTrial();

    return () => {
      isMounted = false;
    };
  }, []); // Remove navigate from dependencies - it's stable and doesn't need to trigger re-runs

  // Function to handle settings button click
  const _handleSettingsClick = () => {
    setShowSettingsModal(true);
    setShowProfileDropdown(false);
  };

  // Handle direct thread deletion (without confirmation modal)
  const handleDirectDeleteThread = async (thread) => {
    setIsDeletingThread(true);

    // Save the thread being deleted for potential rollback
    const deletedThread = thread;
    const wasCurrentThread = currentThreadId === deletedThread.threadId;

    try {
      // First, delete the thread
      const response = await deleteThread(user._id, deletedThread.threadId);

      if (response?.status !== 200) {
        throw new Error(response?.data?.message || "Failed to delete thread");
      }

      // Update UI to remove the deleted thread
      setChatThreads((prevThreads) =>
        prevThreads.filter(
          (thread) => thread.threadId !== deletedThread.threadId
        )
      );

      // Show success message
      toast.success("Thread deleted successfully");

      // Only create a new thread if we deleted the current thread
      if (wasCurrentThread) {
        if (onCurrentThreadDeleted) onCurrentThreadDeleted();
      }
    } catch (error) {
      toast.error(
        error.message || "An error occurred while deleting the thread"
      );

      // Revert the UI if there was an error
      if (deletedThread) {
        setChatThreads((prevThreads) => {
          // Only add back if not already present
          const exists = prevThreads.some(
            (t) => t.threadId === deletedThread.threadId
          );
          return exists ? prevThreads : [...prevThreads, deletedThread];
        });

        // If it was the current thread, navigate back to it
        if (wasCurrentThread) {
          navigate(`/chat/${deletedThread.threadId}`);
        }
      }
    } finally {
      setIsDeletingThread(false);
    }
  };

  // Handle delete all threads
  const handleDeleteAllThreads = async () => {
    setIsDeletingAllThreads(true);

    try {
      // Call the deleteAllThreads API
      const response = await deleteAllThreads(user._id, user.organizationId);

      if (response?.status !== 200) {
        throw new Error(
          response?.data?.message || "Failed to delete all threads"
        );
      }

      // Clear all threads from UI
      setChatThreads([]);

      // Close the modal
      setShowDeleteAllModal(false);

      // Navigate to main chat page
      navigate("/chat");

      // Show success message
      toast.success("All chats deleted successfully");

      // Notify parent that current thread was deleted if there was one
      if (currentThreadId && onCurrentThreadDeleted) {
        onCurrentThreadDeleted();
      }
    } catch (error) {
      toast.error(
        error.message || "An error occurred while deleting all chats"
      );
    } finally {
      setIsDeletingAllThreads(false);
    }
  };

  return (
    <div
      className={`${
        sidebarExpanded ? "w-64" : "w-0 md:w-16"
      } fixed md:relative left-0 top-0 h-full z-50 md:z-auto bg-bg-50 flex flex-col transition-all duration-300 ease-in-out overflow-hidden ${
        sidebarExpanded ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      }`}
    >
      {/* Header with Logo */}
      <div
        className={`flex items-center ${
          sidebarExpanded
            ? "justify-between pt-[30px] px-3"
            : "justify-center pt-[30px] px-2 pr-4"
        } pb-2`}
      >
        <div
          className={`flex items-center ${sidebarExpanded ? "px-1" : "pl-2"}`}
        >
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              {sidebarExpanded ? (
                <LogoWithText
                  className="h-7 w-auto max-w-32"
                  fillClassName="fill-fg-40 hover:fill-fg-30 transition-colors"
                />
              ) : (
                <LogoIcon
                  className="w-7 h-7"
                  fillClassName="fill-fg-40 hover:fill-fg-30 transition-colors"
                />
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* New Thread + Tools + Search - minimal list style */}
      <div className={`${sidebarExpanded ? "px-2" : "px-1"} py-2`}>
        <button
          onClick={createNewThread}
          className={`w-full flex items-center ${
            sidebarExpanded ? "gap-2 px-2.5" : "justify-center px-2"
          } py-2 rounded-md hover:bg-bg-40 hover:text-fg-50 text-fg-60 transition-colors`}
          title={!sidebarExpanded ? "New chat" : ""}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
          >
            <g fill="none">
              <path
                fill="currentColor"
                fillOpacity=".16"
                d="M18.6 3H5.4A2.4 2.4 0 0 0 3 5.4v13.2A2.4 2.4 0 0 0 5.4 21h13.2a2.4 2.4 0 0 0 2.4-2.4V5.4A2.4 2.4 0 0 0 18.6 3"
              />
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="10"
                strokeWidth="1.5"
                d="M8 12h8m-4 4V8M5.4 3h13.2A2.4 2.4 0 0 1 21 5.4v13.2a2.4 2.4 0 0 1-2.4 2.4H5.4A2.4 2.4 0 0 1 3 18.6V5.4A2.4 2.4 0 0 1 5.4 3"
              />
            </g>
          </svg>
          {sidebarExpanded && (
            <span className="text-sm font-medium">New chat</span>
          )}
        </button>
        {/* Tools button - commented out */}
        {/* <button
           onClick={() => {
             if (typeof onOpenTools === "function") onOpenTools();
           }}
           className={`mt-0.5 w-full flex items-center ${
             sidebarExpanded ? "gap-2 px-2.5" : "justify-center px-2"
           } py-2 rounded-md hover:bg-bg-40 hover:text-fg-50 text-fg-60 transition-colors`}
           title={!sidebarExpanded ? "Tools" : ""}
         >
           <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
            >
             <g fill="none">
               <path
                 fill="currentColor"
                 fillOpacity=".16"
                 d="M8.4 4H5.6A1.6 1.6 0 0 0 4 5.6v2.8A1.6 1.6 0 0 0 5.6 10h2.8A1.6 1.6 0 0 0 10 8.4V5.6A1.6 1.6 0 0 0 8.4 4m10 10h-2.8a1.6 1.6 0 0 0-1.6 1.6v2.8a1.6 1.6 0 0 0 1.6 1.6h2.8a1.6 1.6 0 0 0 1.6-1.6v-2.8a1.6 1.6 0 0 0-1.6-1.6"
               />
               <path
                 stroke="currentColor"
                 strokeLinejoin="round"
                 strokeMiterlimit="10"
                 strokeWidth="1.5"
                 d="M8.4 4H5.6A1.6 1.6 0 0 0 4 5.6v2.8A1.6 1.6 0 0 0 5.6 10h2.8A1.6 1.6 0 0 0 10 8.4V5.6A1.6 1.6 0 0 0 8.4 4Zm0 10H5.6A1.6 1.6 0 0 0 4 15.6v2.8A1.6 1.6 0 0 0 5.6 20h2.8a1.6 1.6 0 0 0 1.6-1.6v-2.8A1.6 1.6 0 0 0 8.4 14Zm10-10h-2.8A1.6 1.6 0 0 0 14 5.6v2.8a1.6 1.6 0 0 0 1.6 1.6h2.8A1.6 1.6 0 0 0 20 8.4V5.6A1.6 1.6 0 0 0 18.4 4Zm0 10h-2.8a1.6 1.6 0 0 0-1.6 1.6v2.8a1.6 1.6 0 0 0 1.6 1.6h2.8a1.6 1.6 0 0 0 1.6-1.6v-2.8a1.6 1.6 0 0 0-1.6-1.6Z"
               />
             </g>
           </svg>
           {sidebarExpanded && (
             <span className="text-sm font-medium">Tools</span>
           )}
         </button> */}

        {/* Inline Search */}
        <div
          className={`mt-0.5 w-full flex items-center ${
            sidebarExpanded ? "gap-[5px] px-2.5" : "justify-center px-2"
          } py-2 rounded-md hover:bg-bg-40 hover:text-fg-50 text-fg-60 placeholder:text-fg-60 transition-colors font-medium cursor-pointer`}
          onClick={() => {
            if (!sidebarExpanded && setSidebarExpanded) {
              // Expand sidebar when search is clicked in collapsed mode
              setSidebarExpanded(true);
            }
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="19"
            className="flex-shrink-0"
            viewBox="0 0 24 24"
          >
            <g fill="none">
              <path
                fill="currentColor"
                fillOpacity=".16"
                d="M11 19a8 8 0 1 0 0-16a8 8 0 0 0 0 16"
              />
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="10"
                strokeWidth="1.5"
                d="m21 21l-4-4m2-6a8 8 0 1 1-16 0a8 8 0 0 1 16 0"
              />
            </g>
          </svg>

          {sidebarExpanded && (
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search chats"
              className="flex-1 bg-transparent text-sm text-fg-50 placeholder:text-fg-60 hover:text-fg-50 border-0 focus:outline-none focus:ring-0 font-medium"
            />
          )}
        </div>
      </div>

      {/* Chat History */}
      {sidebarExpanded && (
        <div className="flex-grow overflow-y-auto custom-scrollbar">
          <div className="px-3 py-2">
            <div className="flex items-center justify-between mb-3 group">
              <h3 className="text-xs font-semibold text-fg-60 tracking-wider px-2">
                Chats
              </h3>
              <div className="flex items-center gap-1">
                {chatHistory && chatHistory.length > 0 && (
                  <button
                    onClick={() => setShowDeleteAllModal(true)}
                    disabled={isLoadingHistory}
                    className="p-1 text-fg-60 hover:text-fg-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete all chats"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 256 256"
                    >
                      <g fill="currentColor">
                        <path
                          d="M200 56v152a8 8 0 0 1-8 8H64a8 8 0 0 1-8-8V56Z"
                          opacity=".2"
                        />
                        <path d="M216 48h-40v-8a24 24 0 0 0-24-24h-48a24 24 0 0 0-24 24v8H40a8 8 0 0 0 0 16h8v144a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16V64h8a8 8 0 0 0 0-16M96 40a8 8 0 0 1 8-8h48a8 8 0 0 1 8 8v8H96Zm96 168H64V64h128Zm-80-104v64a8 8 0 0 1-16 0v-64a8 8 0 0 1 16 0m48 0v64a8 8 0 0 1-16 0v-64a8 8 0 0 1 16 0" />
                      </g>
                    </svg>
                  </button>
                )}
                <button
                  onClick={refreshThreadList}
                  disabled={isLoadingHistory}
                  className="p-1 text-fg-60 hover:text-fg-50 disabled:opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Refresh threads"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 256 256"
                  >
                    <g fill="currentColor">
                      <path
                        d="M216 128a88 88 0 1 1-88-88a88 88 0 0 1 88 88"
                        opacity=".2"
                      />
                      <path d="M240 56v48a8 8 0 0 1-8 8h-48a8 8 0 0 1 0-16h27.4l-26.59-24.36l-.25-.24a80 80 0 1 0-1.67 114.78a8 8 0 0 1 11 11.63A95.44 95.44 0 0 1 128 224h-1.32a96 96 0 1 1 69.07-164L224 85.8V56a8 8 0 1 1 16 0" />
                    </g>
                  </svg>
                </button>
              </div>
            </div>

            {isLoadingHistory ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="animate-pulse flex items-center p-2 rounded-md bg-bg-40"
                  >
                    <div className="h-4 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : chatHistory && chatHistory.length > 0 ? (
              <ul className="space-y-1">
                {chatHistory.map((thread) => {
                  const isActive = currentThreadId === thread.threadId;
                  const threadTitle = thread.name || "Untitled Chat";

                  return (
                    <li
                      key={thread.threadId}
                      className="relative group"
                      onMouseEnter={() => setHoveredThread(thread.threadId)}
                      onMouseLeave={() => setHoveredThread(null)}
                    >
                      <div
                        className={`group flex items-center w-full rounded-md ${
                          isActive
                            ? "bg-bg-40 font-medium"
                            : hoveredThread === thread.threadId
                            ? "bg-bg-50"
                            : ""
                        }`}
                      >
                        <button
                          onClick={() => handleChatSelect(thread)}
                          className={`flex-1 min-w-0 text-left px-3 py-2 text-sm ${
                            isActive
                              ? "text-fg-50"
                              : "text-fg-60 hover:text-fg-50"
                          }`}
                          title={threadTitle}
                        >
                          <div className="flex items-center w-full">
                            <span className="truncate font-medium text-sm">
                              {threadTitle}
                            </span>
                          </div>
                        </button>

                        {/* Delete button - only shown on hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDirectDeleteThread(thread);
                          }}
                          disabled={isDeletingThread}
                          className={`flex-shrink-0 p-1 text-fg-60 hover:text-red-500 transition-all duration-200 mr-1 ${
                            hoveredThread === thread.threadId
                              ? "opacity-100"
                              : "opacity-0 group-hover:opacity-100"
                          } ${
                            isDeletingThread
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          title="Delete thread"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-center py-4">
                <p className="text-fg-60 text-sm mb-2">No chat history</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Profile */}
      {/* Trial remaining days banner - shown above profile container (only in expanded mode) */}
      {sidebarExpanded &&
        !hideTrialBanner &&
        trialInfo?.status === "progress" &&
        typeof trialInfo.daysRemaining === "number" && (
          <div className="px-3 mt-2 mb-2">
            {/* Two-point side-to-side gradient background with matching border/text */}
            <div className="relative rounded-2xl overflow-hidden border bg-[#ece4f7] dark:bg-[#2d1b3d] border-[#A758DB]">
              {/* content row */}
              <div className="relative z-10 flex items-center gap-3 p-3">
                <div className="flex-1">
                  <p className="text-xs text-[#852bc1] dark:text-[#c084fc]">
                    Free Trial
                  </p>
                  <p className="text-sm font-normal text-fg-40">
                    {trialInfo.daysRemaining} days remaining
                  </p>
                </div>
                {/* close button to hide until next tab refresh */}
                <button
                  onClick={() => setHideTrialBanner(true)}
                  className="p-1.5 rounded-full text-[#A758DB] transition-colors border border-transparent hover:text-[#8935c1]"
                  title="Dismiss"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 256 256"
                  >
                    <g fill="currentColor">
                      <path d="M200 200a8 8 0 0 1-11.31 0L128 139.31L67.31 200A8 8 0 0 1 56 188.69L116.69 128L56 67.31A8 8 0 0 1 67.31 56L128 116.69L188.69 56A8 8 0 0 1 200 67.31L139.31 128Z" />
                    </g>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

      <div className="mt-auto">
        {/* Hide profile dropdown for anonymous users */}
        {!isAnonymous && (
          <div
            className={`relative ${sidebarExpanded ? "p-3" : "p-2"}`}
            ref={profileDropdownRef}
          >
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className={`w-full flex items-center p-2 rounded-lg hover:bg-bg-50/30 focus:bg-bg-50/40 transition-colors duration-150 outline-none ring-0 group ${
                !sidebarExpanded ? "justify-center" : ""
              }`}
              tabIndex={0}
              aria-haspopup="true"
              aria-expanded={showProfileDropdown}
              title={!sidebarExpanded ? name || "User" : ""}
            >
              <div className="w-8 h-8 rounded-full bg-ac-02 flex items-center justify-center text-white dark:text-black text-sm font-medium flex-shrink-0 group-hover:scale-105 transition-transform duration-150">
                {name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              {sidebarExpanded && (
                <div className="ml-3 text-left overflow-hidden">
                  <p className="text-sm font-medium truncate">
                    {name || "User"}
                  </p>
                  <p className="text-xs text-fg-60 truncate">
                    {email || "user@example.com"}
                  </p>
                </div>
              )}
              {sidebarExpanded && (
                <div className="ml-auto">
                  {showProfileDropdown ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </div>
              )}
            </button>

            {showProfileDropdown && sidebarExpanded && (
              <div
                className="absolute bottom-full left-0 right-0 mb-2 z-50 min-w-[200px] px-2"
                onMouseLeave={() => setShowProfileDropdown(false)}
                style={{ pointerEvents: "auto" }}
              >
                <div className="bg-bg-60 border border-bd-50 shadow-lg rounded-lg py-1 transition-all duration-200 animate-fade-in">
                  <div className="px-4 py-2">
                    <p className="text-sm font-semibold text-fg-50 truncate">
                      {name || "User"}
                    </p>
                    <p className="text-xs text-fg-60 truncate">
                      {email || "user@example.com"}
                    </p>
                  </div>
                  <button
                    onClick={_handleSettingsClick}
                    className="w-full text-left px-4 py-2 text-sm text-fg-60 hover:bg-bg-40 hover:text-fg-80 focus:bg-bg-40 focus:text-fg-80 transition-colors flex items-center gap-2 rounded"
                  >
                    <Settings
                      size={16}
                      className="text-fg-60 group-hover:text-fg-80"
                    />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-100 hover:text-red-600 focus:bg-red-100 focus:text-red-600 transition-colors flex items-center gap-2 rounded"
                  >
                    <LogOut
                      size={16}
                      className="text-red-400 group-hover:text-red-600"
                    />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Back to homepage button for anonymous users */}
        {isAnonymous && (
          <div className={`${sidebarExpanded ? "p-3" : "p-2"}`}>
            <button
              onClick={() => {
                // Navigate to homepage
                navigate("/");
              }}
              className={`w-full flex items-center p-2 rounded-lg hover:bg-bg-50/30 focus:bg-bg-50/40 transition-colors duration-150 outline-none ring-0 ${
                !sidebarExpanded ? "justify-center" : ""
              }`}
              title={!sidebarExpanded ? "Back to Home" : ""}
            >
              <ArrowLeft
                size={18}
                className="text-fg-60 hover:text-fg-50 flex-shrink-0"
              />
              {sidebarExpanded && (
                <span className="ml-3 text-sm text-fg-60 hover:text-fg-50 font-medium">
                  Back to Home
                </span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Delete All Threads Modal */}
      <DeleteThreadModal
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={handleDeleteAllThreads}
        threadName="all chats"
        isLoading={isDeletingAllThreads}
        isDeleteAll={true}
      />
    </div>
  );
};

export default ChatSidebar;
